/**
 * Summary surfaces: highlight band, stat/metric chips, catalog media cards, empty state, bottom sheets.
 *
 * **Consumer map:** `JourneyResultScreen`, `LoyaltyScreen` (highlight band + metric typography on primary hero).
 * Add `Used by:` on new exports when a third consumer appears.
 *
 * @see atomicRules.ts
 */
import { StyleSheet, ViewStyle, TextStyle, ImageStyle } from 'react-native';
import type { ThemeColors } from '../store';
import { appTheme } from '../appTheme';

export const summaryScreenRoot: ViewStyle = {
  flex: 1,
};

export const summaryLoaderViewport: ViewStyle = {
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  paddingHorizontal: appTheme.spacing.md,
};

export function createSummaryLoaderCaptionStyle(c: ThemeColors): TextStyle {
  return {
    ...appTheme.typography.body,
    marginTop: appTheme.spacing.lg,
    color: c.textSecondary,
  };
}

export const summaryScroll: ViewStyle = {
  flex: 1,
};

export const summaryHighlightBand: ViewStyle = {
  marginBottom: appTheme.spacing.md,
  borderRadius: appTheme.borderRadius.lg,
  overflow: 'hidden',
  ...appTheme.shadows.md,
};

export const summaryHighlightBandContent: ViewStyle = {
  paddingVertical: appTheme.spacing.lg,
  paddingHorizontal: appTheme.card.padding.lg,
  alignItems: 'center',
};

export const summaryHighlightEyebrow: TextStyle = {
  fontSize: 12,
  fontWeight: '800',
  color: 'rgba(255,255,255,0.92)',
  marginBottom: 8,
  letterSpacing: 1.2,
  textTransform: 'uppercase',
};

export const summaryHighlightMetric: TextStyle = {
  fontSize: 56,
  fontWeight: '800',
  letterSpacing: -2,
};

export const summaryHighlightUnit: TextStyle = {
  fontSize: 15,
  fontWeight: '600',
  color: 'rgba(255,255,255,0.88)',
  marginTop: 6,
};

export const summaryStatPillRow: ViewStyle = {
  flexDirection: 'row',
  gap: appTheme.spacing.md,
  marginBottom: appTheme.spacing.md,
  justifyContent: 'center',
  alignItems: 'stretch',
  width: '100%',
};

export function createSummaryStatPillSurfaceStyle(c: ThemeColors): ViewStyle {
  return {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: appTheme.spacing.md,
    paddingHorizontal: appTheme.spacing.sm,
    borderRadius: appTheme.borderRadius.lg,
    gap: 8,
    backgroundColor: c.surface,
    shadowColor: c.shadowColor,
    ...appTheme.shadows.xs,
  };
}

export function createSummaryStatValueStyle(opts: { color: string }): TextStyle {
  return {
    fontSize: 20,
    fontWeight: '800',
    color: opts.color,
  };
}

export function createSummaryStatCaptionStyle(c: ThemeColors): TextStyle {
  return {
    ...appTheme.typography.caption,
    fontWeight: '700',
    fontSize: 11,
    color: c.textSecondary,
  };
}

export function createSummaryStatusDotStyle(opts: { color: string }): ViewStyle {
  return {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: opts.color,
  };
}

export const summaryMetricChipRow: ViewStyle = {
  flexDirection: 'row',
  gap: appTheme.spacing.sm,
  marginBottom: appTheme.spacing.lg,
};

export function createSummaryMetricChipSurfaceStyle(c: ThemeColors): ViewStyle {
  return {
    flex: 1,
    alignItems: 'center',
    paddingVertical: appTheme.spacing.md,
    borderRadius: appTheme.borderRadius.lg,
    backgroundColor: c.surface,
    shadowColor: c.shadowColor,
    ...appTheme.shadows.xs,
  };
}

export function createSummaryMetricChipValueStyle(opts: { color: string }): TextStyle {
  return {
    fontSize: 16,
    fontWeight: '800',
    color: opts.color,
  };
}

export function createSummaryMetricChipCaptionStyle(c: ThemeColors): TextStyle {
  return {
    fontSize: 11,
    marginTop: 6,
    fontWeight: '700',
    color: c.textSecondary,
  };
}

export const summaryCatalogSection: ViewStyle = {
  paddingBottom: appTheme.spacing.xl,
};

export function createSummarySectionHeadingStyle(c: ThemeColors): TextStyle {
  return {
    ...appTheme.typography.h3,
    marginBottom: appTheme.spacing.lg,
    fontWeight: '800',
    color: c.text,
  };
}

export const summaryCatalogCardSpacer: ViewStyle = {
  marginBottom: appTheme.spacing.md,
};

export function createSummaryCatalogCardSurfaceStyle(c: ThemeColors): ViewStyle {
  return {
    borderRadius: appTheme.borderRadius.lg,
    borderWidth: 1,
    overflow: 'hidden',
    backgroundColor: c.surface,
    borderColor: c.border,
    shadowColor: c.shadowColor,
    ...appTheme.shadows.md,
  };
}

export const summaryCatalogCardMediaFrame: ViewStyle = {
  height: 188,
  position: 'relative',
};

export const summaryCatalogCardImage: ImageStyle = {
  width: '100%',
  height: '100%',
};

export const summaryCatalogCardMediaScrim: ViewStyle = {
  ...StyleSheet.absoluteFillObject,
  backgroundColor: 'transparent',
};

export const summaryCatalogCardBadgeStack: ViewStyle = {
  position: 'absolute',
  top: appTheme.spacing.md,
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
};

export function createSummaryPromoBadgeSurfaceStyle(c: ThemeColors): ViewStyle {
  return {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    backgroundColor: c.error,
  };
}

export function createSummaryPromoBadgeLabelStyle(c: ThemeColors): TextStyle {
  return {
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: c.onError ?? c.surface,
  };
}

export function createSummaryPriceBadgeSurfaceStyle(c: ThemeColors): ViewStyle {
  return {
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 12,
    backgroundColor: c.primary + 'E8',
  };
}

export function createSummaryPriceBadgeLabelStyle(c: ThemeColors): TextStyle {
  return {
    fontSize: 14,
    fontWeight: '800',
    color: c.onPrimary,
  };
}

export const summaryCatalogCardBody: ViewStyle = {
  padding: appTheme.card.padding.lg,
};

export function createSummaryCatalogCardTitleStyle(c: ThemeColors): TextStyle {
  return {
    ...appTheme.typography.bodyMedium,
    fontWeight: '800',
    marginBottom: 12,
    fontSize: 17,
    lineHeight: 24,
    color: c.text,
  };
}

export const summaryCatalogCardMetaRow: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: appTheme.spacing.sm,
};

export const summaryCatalogCardMetaCluster: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
};

export function createSummaryCatalogMetaCaptionStyle(c: ThemeColors): TextStyle {
  return {
    ...appTheme.typography.caption,
    fontWeight: '700',
    color: c.textSecondary,
  };
}

export function createSummaryCatalogDurationCaptionStyle(c: ThemeColors): TextStyle {
  return {
    ...appTheme.typography.caption,
    fontWeight: '700',
    color: c.textSecondary,
  };
}

export function createSummaryCatalogInlineCtaSurfaceStyle(c: ThemeColors): ViewStyle {
  return {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginStart: 'auto',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: appTheme.borderRadius.md,
    gap: 6,
    backgroundColor: c.primary,
  };
}

export function createSummaryCatalogInlineCtaLabelStyle(c: ThemeColors): TextStyle {
  return {
    fontSize: 13,
    fontWeight: '800',
    color: c.onPrimary,
  };
}

export const summaryRtlRowReverse: ViewStyle = {
  flexDirection: 'row-reverse',
};

export function createSummaryEmptyStateCardStyle(c: ThemeColors): ViewStyle {
  return {
    alignItems: 'center',
    padding: 40,
    borderRadius: appTheme.borderRadius.lg,
    backgroundColor: c.surface,
    shadowColor: c.shadowColor,
    ...appTheme.shadows.xs,
  };
}

export function createSummaryEmptyStateIconWellStyle(c: ThemeColors): ViewStyle {
  return {
    width: 88,
    height: 88,
    borderRadius: 44,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: appTheme.spacing.lg,
    backgroundColor: c.primaryFade,
  };
}

export function createSummaryEmptyStateTitleStyle(c: ThemeColors): TextStyle {
  return {
    ...appTheme.typography.h3,
    marginBottom: 8,
    fontWeight: '800',
    color: c.text,
  };
}

export function createSummaryEmptyStateSubtitleStyle(c: ThemeColors): TextStyle {
  return {
    ...appTheme.typography.bodySmall,
    marginBottom: appTheme.spacing.xl,
    textAlign: 'center',
    lineHeight: 22,
    color: c.textSecondary,
  };
}

/** Solid CTA — wrap with `Clickable` + `buttonWrap` + `[button, buttonFilled, dynamic bg]`. */
export const summarySolidCtaPadding: ViewStyle = {
  paddingVertical: 16,
  paddingHorizontal: 40,
};

export const sheetStackingOverlayRoot: ViewStyle = {
  flex: 1,
  justifyContent: 'flex-end',
};

export function createSheetStackingPanelSurfaceStyle(c: ThemeColors): ViewStyle {
  return {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: appTheme.spacing.xl,
    backgroundColor: c.surface,
  };
}

export function createSheetStackingTitleStyle(c: ThemeColors): TextStyle {
  return {
    ...appTheme.typography.h2,
    marginBottom: appTheme.spacing.sm,
    fontWeight: '800',
    color: c.text,
  };
}

export function createSheetStackingBodyStyle(c: ThemeColors): TextStyle {
  return {
    ...appTheme.typography.body,
    marginBottom: appTheme.spacing.lg,
    lineHeight: 22,
    color: c.textSecondary,
  };
}

export const sheetStackingPrimaryActionPadding: ViewStyle = {
  paddingVertical: 16,
  borderRadius: appTheme.borderRadius.lg,
  alignItems: 'center',
  marginBottom: appTheme.spacing.sm,
};

export const sheetStackingPrimaryActionLabel: TextStyle = {
  ...appTheme.typography.body,
  fontWeight: '800',
};

export function createSheetStackingSecondaryActionSurfaceStyle(c: ThemeColors): ViewStyle {
  return {
    paddingVertical: 14,
    alignItems: 'center',
    borderRadius: appTheme.borderRadius.lg,
    borderWidth: 1,
    borderColor: c.border,
  };
}

export function createSheetStackingSecondaryActionLabelStyle(c: ThemeColors): TextStyle {
  return {
    ...appTheme.typography.body,
    fontWeight: '700',
    color: c.primary,
  };
}

export const sheetRadioStackingOverlayRoot: ViewStyle = {
  flex: 1,
  justifyContent: 'flex-end',
};

export function createSheetRadioPanelSurfaceStyle(c: ThemeColors): ViewStyle {
  return {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: appTheme.spacing.xl,
    backgroundColor: c.surface,
  };
}

export function createSheetRadioPanelTitleStyle(c: ThemeColors): TextStyle {
  return {
    ...appTheme.typography.h3,
    marginBottom: appTheme.spacing.sm,
    fontWeight: '800',
    color: c.text,
  };
}

export function createSheetRadioPanelMessageStyle(c: ThemeColors): TextStyle {
  return {
    ...appTheme.typography.body,
    marginBottom: appTheme.spacing.md,
    lineHeight: 22,
    color: c.textSecondary,
  };
}

export function createSheetRadioPanelAccentHighlightStyle(c: ThemeColors): TextStyle {
  return {
    fontWeight: '800',
    color: c.primary,
  };
}

export const sheetRadioOptionList: ViewStyle = {
  gap: appTheme.spacing.md,
  marginBottom: appTheme.spacing.lg,
};

export function createSheetRadioRowSurfaceStyle(c: ThemeColors): ViewStyle {
  return {
    flexDirection: 'row',
    alignItems: 'center',
    padding: appTheme.card.padding.lg,
    borderRadius: appTheme.borderRadius.lg,
    borderWidth: 2,
    gap: appTheme.spacing.sm,
    borderColor: c.border,
    backgroundColor: c.surface,
    shadowColor: c.shadowColor,
    ...appTheme.shadows.sm,
  };
}

export function createSheetRadioRowSelectedOverlayStyle(c: ThemeColors): ViewStyle {
  return {
    borderColor: c.primary,
    backgroundColor: c.primaryUltra,
  };
}

export function createSheetRadioOuterDiscStyle(c: ThemeColors, opts: { selected: boolean }): ViewStyle {
  return {
    width: 26,
    height: 26,
    borderRadius: 13,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
    borderColor: opts.selected ? c.primary : c.border,
  };
}

export function createSheetRadioInnerDiscStyle(c: ThemeColors): ViewStyle {
  return {
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: c.primary,
  };
}

export function createSheetRadioRowCaptionStyle(
  c: ThemeColors,
  opts: { active: boolean },
): TextStyle {
  return {
    ...appTheme.typography.body,
    flex: 1,
    color: opts.active ? c.text : c.textSecondary,
    fontWeight: opts.active ? '700' : '400',
  };
}

export const sheetRadioConfirmActionPadding: ViewStyle = {
  paddingVertical: 16,
  borderRadius: appTheme.borderRadius.lg,
  alignItems: 'center',
};

export const sheetRadioConfirmActionLabel: TextStyle = {
  ...appTheme.typography.bodyMedium,
  fontWeight: '800',
};
